import { BaseIdType } from '@globalmed/atlas-ui';
import {
  ELanguage,
  ITemplateResponse,
  ITemplateResponsePagination,
  TConvertKeys,
  TNullable,
} from './common';
import { IUploadDocumentData } from './documents';
import { TFormBuilderEntry, TFormBuilderEntryField } from './formBuilder';
import { IAppointmentResultFormData } from './infermedica';
import { IParticipant } from './participants';
import { EUserRoles, TUserSummary } from './user';

export type TCommunicationChannel = 'video' | 'chat';
export type TScheduleType = 'on_demand' | 'scheduled';
export type TAvailabilityStatus =
  'online' | 'offline' | 'in_meeting' | 'in_waiting_room';

export interface ICreateAppointmentRequest {
  institution_id: string;
  infermedica_survey_id?: string;
  isabel_survey_id?: string;
  institution_service_id: string;
  location?: {
    country: string;
    subdivision: string;
  };
  communication_channel: TCommunicationChannel;
  schedule_type: TScheduleType;
  start?: string;
  duration: number;
  insurance?: {
    id: string;
    is_premium: boolean;
    status?: string[];
  };
  consented: boolean;
  document_ids?: string[];
  user_institution_role_ids?: string[];
  manual_symptoms?: IManualSymptoms;
  kiosk_id?: TNullable<string>;
}

interface ISymptom {
  id: string;
  name: string;
  choice_id: string;
  seriousness: string;
  source: string;
  question_id: string;
}

type TIsabelTriageFlagString = 'true' | 'false';

export type TAppointmentIsabelTriageDiagnosis = {
  redFlag: TIsabelTriageFlagString;
  specialty: string;
  diagnosisId: number;
  diagnosisName: string;
  englishDiagnosisName: string;
  spanishDiagnosisName: string;
  portugueseDiagnosisName: string;
  commonDiagnosis: TIsabelTriageFlagString;
  icd9DiagnosisId: string;
  icd10DiagnosisId: string;
  snomedDiagnosisId: string;
  knowledgeWindowApiUrl: string;
};

type TAppointmentIsabelTriageQueryResultDetails = {
  gender: string;
  region: string;
  sorting: string;
  ageGroup: string;
  pregnancy: string;
  queryEntered: string;
  englishProcessedQuery: string;
  specialtyType: string;
  totalResultsReturned: string;
};

export type TAppointmentIsabelTriageData = {
  summary: {
    diagnoses: TAppointmentIsabelTriageDiagnosis[];
    triageApiUrl: string;
    queryResultDetails: TAppointmentIsabelTriageQueryResultDetails;
  };
  language: ELanguage;
  triageScore: number;
  specialistRecommendation: string;
};

interface IIsabelTriageResults {
  provider: 'isabel';
  data: TAppointmentIsabelTriageData[];
}

interface IInfermedicaTriageResults {
  provider: 'infermedica';
  data: IInfermedicaSurvey[];
}

export type ITriageResults = IInfermedicaTriageResults | IIsabelTriageResults;
export type TTriageResultsCamel = TConvertKeys<ITriageResults>;

export interface IStatus<TAction = EStatusAppointment> {
  at: string;
  by: string;
  action: TAction;
  user?: Partial<TUserSummary>;
  meta?: {
    start?: string;
    end?: string;
    original_start?: string;
    original_end?: string;
    reason?: string;
  };
}

export type TStatusCamel = TConvertKeys<IStatus>;

export enum EStatusAppointment {
  PENDING_INSURANCE = 'pending_insurance',
  PENDING_CONFIRMATION = 'pending_confirmation',
  PENDING_FORMS = 'pending_forms',

  PAYMENT_REQUIRED = 'payment_required',
  COPAY_REQUIRED = 'copay_required',

  CONFIRMED = 'confirmed',
  CANCELED = 'canceled',
  HELD = 'held',
}

export enum ECoordinatorSessionStatus {
  TO_BE_CONFIRMED = 'to_be_confirmed',
  NO_ANSWER = 'no_answer',
  CONFIRMED = 'confirmed',
  TO_BE_RESCHEDULED = 'to_be_rescheduled',
  TO_BE_CANCELLED = 'to_be_canceled',
  CANCELLED = 'canceled',
}

export type TCoordinatorOutreachStatus = Exclude<
  ECoordinatorSessionStatus,
  ECoordinatorSessionStatus.CANCELLED
>;

export interface IOutreachNote {
  text: string;
  by: string;
  at: string;
  user?: Partial<TUserSummary>;
}

export interface IUpdateOutreachStatusRequest {
  status: TCoordinatorOutreachStatus;
  note?: string;
}

export interface IAddOutreachNoteRequest {
  text: string;
}

type TVisitNoteSchemaCommon = {
  title: string;
  fields: TFormBuilderEntryField[];
};

export interface IVisitNote {
  template_id: string;
  created_at?: string;
  data: TVisitNoteSchemaCommon;
}

export interface ICreateVisitNoteRequest {
  template_id: string;
  data: TVisitNoteSchemaCommon;
  signature?: { name: string };
}

export interface IManualNote extends IVisitNote {
  id: string;
  patient_user_institution_role_id: string;
  user_institution_role_id: string;
  user?: Partial<TUserSummary>;
}

export interface ICreateManualNoteRequest extends ICreateVisitNoteRequest {
  user_institution_role_id: string;
}

export type IGetManualNotesResponse = ITemplateResponse<IManualNote[]>;
export type ICreateManualNoteResponse = ITemplateResponse<IManualNote>;

export type IGetAppointmentsResponse = ITemplateResponse<
  IAppointment[],
  ITemplateResponsePagination
>;

export type TAppointmentKioskPolling = {
  id: string;
  start: string;
  end: string;
};

export interface IGetOneAppointmentResponse {
  data: {
    data: IAppointment;
  };
}

interface IAppointmentWaitingRoom {
  id: string;
  unread_messages: number;
  started_at: string;
  ended_at: string;
}

export interface IAppointment {
  participants: IParticipant[];
  id: string;
  institution_id: string;
  triage_results: TNullable<ITriageResults>;
  institution_service_id: string;
  encounter_id: string;
  location: {
    country: string;
    subdivision: string;
  };
  communication_channel: TCommunicationChannel;
  schedule_type: TScheduleType;
  start?: string;
  end?: string;
  insurance?: TNullable<{
    id: string;
    is_premium: boolean;
    status?: string[];
  }>;
  consented: boolean;
  documents: IUploadDocumentData[];
  discharge_instructions?: string;
  visit_notes?: IVisitNote[];
  status: IStatus[];
  created_at: string;
  created_by?: Partial<TUserSummary>;
  updated_at: string;
  real_duration: number;
  details: {
    actual_start: string;
    actual_end: string;
    actual_duration: number;
    active_since: TNullable<string>;
    last_active: TNullable<string>;
  };
  chat_id: string;
  manual_symptoms: IManualSymptoms;
  payment_id?: string;
  waiting_room: TNullable<Partial<IAppointmentWaitingRoom>>;
  session_id: string;
  form_entries: TFormBuilderEntry[];
  needs_follow_up: TNullable<boolean>;
  canary_assessment?: TNullable<TAppointmentCanaryAssessment>;
  kiosk_id?: TNullable<string>;
  outreach_status?: IStatus<ECoordinatorSessionStatus>[];
  outreach_notes?: IOutreachNote[];
}

export interface IPatchAppointmentRequest {
  discharge_instructions?: string;
  document_ids?: string[];
  insurance?: IAppointment['insurance'];
  user_institution_role_id?: string;
  needs_follow_up?: boolean;
}

export enum EAppointmentEvents {
  PATIENT_EXPECTATIONS_RATE = 'patient_expectation_rate',
  PATIENT_QUALITY_RATE = 'patient_quality_rate',
  PROVIDER_QUALITY_RATE = 'provider_quality_rate',
  CANARY_REQUEST = 'canary_request',
  MEDWAND_REQUEST = 'medwand_request',
}

export interface IPostAppointmentFeedbackRequest {
  type: EAppointmentEvents;
  score?: number;
}

export interface IPutAppointmentParticipant {
  user_institution_role_id: string;
  is_invited?: boolean;
}

export interface TGetAppointmentsParams {
  institution_id: string;
  role: EUserRoles;
  // Pagination: page is 1-indexed, per_page = -1 returns all results
  page?: number;
  per_page?: number;
}

export type DurationUnit = 'hours' | 'days' | 'months' | 'years';

interface IDuration {
  unit: DurationUnit;
  value: number;
}

export interface IManualSymptoms {
  symptom_ids: BaseIdType[];
  duration?: IDuration;
  other: string;
}

export interface ICreateAppointmentResponseData {
  success: boolean;
  error: string | null;
  data: IAppointment;
}

export interface ICreateAppointmentResponse {
  data: ICreateAppointmentResponseData;
}

interface IRescheduleAppointmentRequest {
  start: string;
  duration: number;
  kioskId?: TNullable<string>;
}

interface IInfermedicaSurvey {
  language: string;
  specialist_recommendation: {
    recommended_specialist: string;
    recommended_channel: string;
    specialist: {
      id: string;
      name: string;
    };
    channel: {
      id: string;
      name: string;
    };
  };
  summary: {
    type: string;
    patient: {
      id: string;
      age: {
        value: number;
        unit: string;
      };
      sex: string;
      dependent: boolean;
    };
    present_symptoms: ISymptom[];
    absent_symptoms: ISymptom[];
    skipped_symptoms: ISymptom[];
    risk_factors: ISymptom[];
  };
}

interface IAppointmentButtonSolidColors {
  text: string;
  background: string;
}

export interface IAppointmentButtonColors extends IAppointmentButtonSolidColors {
  highlight1: string;
  highlight2: string;
}

export interface IAppointmentButtonSettings {
  isSolid: boolean;
  colors: IAppointmentButtonColors;
}

export type TAppointmentCanaryScoreCode =
  | 'CognitiveScreening_Overall'
  | 'Depression_Mapped_Overall'
  | 'Anxiety_Mapped_Overall'
  | 'Depression_Category'
  | 'Depression_Overall'
  | 'MCI_Category'
  | 'MCI_Numeric'
  | 'MCI_Overall'
  | 'Anxiety_Category'
  | 'Anxiety_Overall'
  | 'BehavioralScreening_Overall'
  | 'Alzheimer_Category'
  | 'Alzheimer_Overall'
  | 'Alzheimer_Numeric';

type TAppointmentCanaryScoreType = 'COMPOSITE' | 'GENERATED' | 'VOCAL';

type TAppointmentCanaryScore = {
  code: TAppointmentCanaryScoreCode;
  type: TAppointmentCanaryScoreType;
  data: { result: string | number };
};

export type TAppointmentCanaryAssessment = {
  scores: TAppointmentCanaryScore[];
  created_at: string;
  duration?: number; // in seconds
};

export type TAppointmentCamel = TConvertKeys<IAppointment>;
export type TCreateAppointmentRequestCamel =
  TConvertKeys<ICreateAppointmentRequest>;
export type TGetAppointmentsParamsCamel = TConvertKeys<TGetAppointmentsParams>;
export type TPatchAppointmentRequestCamel =
  TConvertKeys<IPatchAppointmentRequest>;
export type TRescheduleAppointmentRequestCamel =
  TConvertKeys<IRescheduleAppointmentRequest>;
export type TVisitNoteCamel = TConvertKeys<IVisitNote>;
export type TCreateVisitNoteRequestCamel =
  TConvertKeys<ICreateVisitNoteRequest>;
export type TManualNoteCamel = TConvertKeys<IManualNote>;
export type TCreateManualNoteRequestCamel =
  TConvertKeys<ICreateManualNoteRequest>;
export type TOutreachNoteCamel = TConvertKeys<IOutreachNote>;
export type TUpdateOutreachStatusRequestCamel =
  TConvertKeys<IUpdateOutreachStatusRequest>;
export type TInfermedicaSurveyCamel = TConvertKeys<IInfermedicaSurvey>;
export type TManualSymptomsCamel = TConvertKeys<IManualSymptoms>;

export interface ICreateAppointmentData extends Omit<
  IAppointmentResultFormData,
  'participants'
> {
  userInstitutionRoleIds: string[];
}
